import { useEffect, useRef, useState } from "react";
import type { VisitorStorage } from "@/lib/visitor-storage";
import type { WidgetFeatures } from "@/lib/widget-config";
import type { WidgetRoute } from "@/lib/widget-route";
import { readPlace, type SavedPlace, savePlace } from "./place-memory";

interface Options {
  /** Per-tab storage (`instance.sessionStorage`; memory-backed for an isolated session). */
  storage: VisitorStorage;
  publicKey: string | null;
  /** Live production session only: false while the config loads and in preview / demo / hidden. */
  enabled: boolean;
  features: WidgetFeatures;
  route: WidgetRoute;
  setRoute: (route: WidgetRoute) => void;
  isOpen: boolean;
  /** The shell's guarded open: waits for config + auth, never opens over an error or a hidden widget. */
  requestOpen: () => void;
  /** `true` on a phone (full-screen panel), `null` until the viewport is known. */
  phone: boolean | null;
  /** The host chose the open state (`open` / `defaultOpen`): it overrides the saved one. */
  hostOwnsOpen: boolean;
  /** The host chose the route (`previewTab` / `Wexio('navigate')`). */
  hostOwnsRoute: boolean;
}

/**
 * Remembers where the visitor was (panel open + route) per browser tab and per public key, and
 * restores it after a host page load. Only the restore of the ROUTE is skipped when the host owns it,
 * and only the OPEN when the host owns that; on a phone the panel never auto-opens (the route is
 * still restored, so the next launcher tap lands where the visitor left).
 */
export function usePlaceMemory({
  storage,
  publicKey,
  enabled,
  features,
  route,
  setRoute,
  isOpen,
  requestOpen,
  phone,
  hostOwnsOpen,
  hostOwnsRoute,
}: Options) {
  const mountRoute = useRef(route);
  const routeRef = useRef(route);
  routeRef.current = route;
  const place = useRef<SavedPlace | null>(null);
  const read = useRef(false);
  const openDone = useRef(false);
  const [settled, setSettled] = useState(false);
  // A restored open is in flight (deferred behind config / auth): keep the stored place meanwhile.
  const holdSave = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a one-shot read once the live session is known; later changes must not re-restore
  useEffect(() => {
    if (!enabled || read.current) return;
    read.current = true;
    place.current = readPlace(storage, publicKey, features);
    const saved = place.current;
    if (saved && !hostOwnsRoute && routeRef.current === mountRoute.current) setRoute(saved.route);
    if (!saved?.open || hostOwnsOpen) setSettled(true);
  }, [enabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one-shot per page load
  useEffect(() => {
    const saved = place.current;
    if (!enabled || !read.current || openDone.current || !saved?.open || hostOwnsOpen) return;
    if (phone === null) return;
    openDone.current = true;
    if (!phone) {
      holdSave.current = true;
      requestOpen();
    }
    setSettled(true);
  }, [enabled, phone]);

  useEffect(() => {
    if (isOpen) holdSave.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!enabled || !settled || (holdSave.current && !isOpen)) return;
    savePlace(storage, publicKey, { open: isOpen, route });
  }, [enabled, settled, isOpen, route, storage, publicKey]);
}
